import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, DollarSign, UploadCloud } from "lucide-react";
import { TIPOS_MODALIDAD } from "@/lib/constants";
import ImportarTarifasDialog from "@/components/config/ImportarTarifasDialog";

export default function TarifasAgenciaDialog({ open, onOpenChange, agencia, onSaved }) {
  const [tours, setTours] = useState([]);
  const [precios, setPrecios] = useState({}); // key: tourId|modalidad|variante -> {a,n,i}
  const [moneda, setMoneda] = useState("USD");
  const [manejaSaldo, setManejaSaldo] = useState(false);
  const [permitidos, setPermitidos] = useState(null); // null = todos
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const cargarTarifas = () => {
    if (!agencia) return;
    setLoading(true);
    Promise.all([api.get("/tours"), api.get(`/agencias/${agencia.id}/tarifas`)])
      .then(([t, tar]) => {
        setTours(t.data.filter((x) => x.activo !== false));
        const map = {};
        (tar.data.tarifas || []).forEach((x) => {
          map[`${x.tour_id}|${x.modalidad}|${x.variante || ""}`] = {
            a: x.precio_adulto || 0, n: x.precio_nino || 0, i: x.precio_infante || 0,
          };
        });
        setPrecios(map);
        setMoneda(tar.data.moneda || agencia.moneda || "USD");
        setManejaSaldo(!!agencia.maneja_saldo_cliente);
        const tp = agencia.tours_permitidos || [];
        setPermitidos(tp.length ? new Set(tp) : null);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !agencia) return;
    cargarTarifas();
  }, [open, agencia]);

  const filas = [];
  tours.forEach((t) => {
    const tipos = [...new Set((t.modalidades || []).map((m) => m.tipo))];
    // Variante SIN/CON BARCO (ej. Cataratas del Niágara): una fila de precio por
    // cada una -- si no, al guardar se perdería el split (el PUT reemplaza TODAS
    // las tarifas de la agencia).
    const variantes = t.requiere_variante_barco ? ["sin_barco", "con_barco"] : [null];
    tipos.forEach((tipo) => variantes.forEach((variante) => filas.push({ tour: t, tipo, variante })));
  });

  const setPrecio = (key, campo, val) => {
    setPrecios((p) => ({ ...p, [key]: { ...(p[key] || { a: 0, n: 0, i: 0 }), [campo]: val } }));
  };

  const toggleTour = (id) => {
    setPermitidos((prev) => {
      const base = prev ? new Set(prev) : new Set(tours.map((t) => t.id));
      if (base.has(id)) base.delete(id);
      else base.add(id);
      return base.size === tours.length ? null : base;
    });
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const tarifas = [];
      Object.entries(precios).forEach(([key, v]) => {
        const [tour_id, modalidad, variante] = key.split("|");
        const a = Number(v.a || 0), n = Number(v.n || 0), iv = Number(v.i || 0);
        if (a || n || iv) tarifas.push({ tour_id, modalidad, variante: variante || null, precio_adulto: a, precio_nino: n, precio_infante: iv });
      });
      await api.put(`/agencias/${agencia.id}`, {
        maneja_saldo_cliente: manejaSaldo,
        moneda,
        tours_permitidos: permitidos ? [...permitidos] : [],
      });
      await api.put(`/agencias/${agencia.id}/tarifas`, { moneda, tarifas });
      toast.success("Tarifas y configuración guardadas.");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tarifas-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center justify-between gap-2 pr-6">
            <span className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" /> Tarifas · {agencia?.nombre}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setImportOpen(true)}
              data-testid="importar-tarifas-agencia-button"
              className="gap-2"
            >
              <UploadCloud className="h-4 w-4" /> Importar (archivo)
            </Button>
          </DialogTitle>
          <DialogDescription>
            Precio por tour y modalidad (adulto / niño / infante). El portal le muestra a la agencia el total calculado.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 inline" /></div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <div>
                  <p className="text-sm font-medium text-slate-800">Maneja saldo del cliente</p>
                  <p className="text-[11px] text-slate-400">Si está apagado, las reservas son facturadas (sin saldo del cliente).</p>
                </div>
                <Switch checked={manejaSaldo} onCheckedChange={setManejaSaldo} data-testid="tarifas-saldo-switch" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Moneda</Label>
                <Input value={moneda} onChange={(e) => setMoneda(e.target.value.toUpperCase())} maxLength={3} data-testid="tarifas-moneda" />
              </div>
            </div>

            <div className="rounded-md border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    {["Tour", "Modalidad", "Variante", "Adulto", "Niño", "Infante"].map((h, i) => (
                      <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filas.map(({ tour, tipo, variante }) => {
                    const key = `${tour.id}|${tipo}|${variante || ""}`;
                    const v = precios[key] || { a: "", n: "", i: "" };
                    return (
                      <TableRow key={key} data-testid={`tarifa-row-${key}`}>
                        <TableCell className="text-sm text-slate-700">{tour.nombre}</TableCell>
                        <TableCell className="text-sm text-slate-600">{TIPOS_MODALIDAD[tipo] || tipo}</TableCell>
                        <TableCell className="text-sm text-slate-600 font-medium">
                          {variante ? (variante === "con_barco" ? "Con barco" : "Sin barco") : "—"}
                        </TableCell>
                        {["a", "n", "i"].map((c) => (
                          <TableCell key={c}>
                            <Input
                              type="number" min={0} className="h-8 w-24"
                              value={v[c] ?? ""}
                              onChange={(e) => setPrecio(key, c, e.target.value)}
                              data-testid={`tarifa-${c}-${key}`}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Tours habilitados ({permitidos ? `${permitidos.size} de ${tours.length}` : "todos"})</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-40 overflow-y-auto rounded-md border border-slate-200 p-3">
                {tours.map((t) => {
                  const checked = !permitidos || permitidos.has(t.id);
                  return (
                    <label key={t.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <Checkbox checked={checked} onCheckedChange={() => toggleTour(t.id)} data-testid={`tour-permitido-${t.id}`} />
                      {t.nombre}
                    </label>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-400">Por defecto, todos los tours están habilitados.</p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="tarifas-cancel">Cancelar</Button>
          <Button onClick={guardar} disabled={saving || loading} data-testid="tarifas-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar tarifas"}
          </Button>
        </DialogFooter>
      </DialogContent>

      <ImportarTarifasDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        tipo="agencia"
        agenciaId={agencia?.id}
        onImported={() => { cargarTarifas(); onSaved && onSaved(); }}
      />
    </Dialog>
  );
}
